/* eslint-disable react-refresh/only-export-components -- toast commands intentionally share the host's external store. */
import { useSyncExternalStore } from "react";
import { AlertCircle, CheckCircle2, Info, X } from "lucide-react";

type ToastTone = "success" | "error" | "info";
type ToastItem = { id: number; tone: ToastTone; message: string };

let items: ToastItem[] = [];
let serial = 0;
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((listener) => listener());
}

function dismiss(id: number) {
  items = items.filter((item) => item.id !== id);
  emit();
}

function push(tone: ToastTone, message: string) {
  const id = ++serial;
  items = [...items, { id, tone, message }].slice(-4);
  emit();
  window.setTimeout(() => dismiss(id), tone === "error" ? 7000 : 4500);
  return id;
}

export const toast = Object.assign((message: string) => push("info", message), {
  success: (message: string) => push("success", message),
  error: (message: string) => push("error", message),
  info: (message: string) => push("info", message),
});

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function snapshot() {
  return items;
}

export function ToastHost() {
  const toasts = useSyncExternalStore(subscribe, snapshot, snapshot);
  return (
    <section className="toast-region" aria-label="Notifications" aria-live="polite" aria-relevant="additions removals">
      {toasts.map((item) => {
        const Icon = item.tone === "success" ? CheckCircle2 : item.tone === "error" ? AlertCircle : Info;
        return <div className={`lattice-toast toast-${item.tone}`} role={item.tone === "error" ? "alert" : "status"} key={item.id}><Icon size={18} /><span>{item.message}</span><button type="button" aria-label="Dismiss notification" onClick={() => dismiss(item.id)}><X size={15} /></button></div>;
      })}
    </section>
  );
}
